import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getClassification, resolvePhoto } from "./metalApi";

const stamp = () => new Date().toISOString().slice(0, 10);

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const rowValues = (c, lang, t) => [
  new Date(c.date || c.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }),
  c.supplier_name || "",
  c.description && c.description !== "Nessuna descrizione" ? c.description : "",
  c.discount_percentage > 0 ? c.discount_percentage : "",
  c.send_status === "sent" ? t.reserved.sent : t.reserved.draft,
  c.created_by || "",
];

const HEADERS = (t) => ["Data", "Fornitore", "Descrizione", "Sconto %", "Stato", "Operatore"];

export const downloadCsv = (list, lang, t) => {
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = "\uFEFF" + [HEADERS(t), ...list.map((c) => rowValues(c, lang, t))]
    .map((r) => r.map(cell).join(";"))
    .join("\r\n");
  saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `storico-metal-class_${stamp()}.csv`);
};

const loadImage = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

const downscale = async (dataUri, maxH = 110) => {
  const img = await loadImage(dataUri);
  const scale = Math.min(1, maxH / img.height);
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const cv = document.createElement("canvas");
  cv.width = width;
  cv.height = height;
  cv.getContext("2d").drawImage(img, 0, 0, width, height);
  return { base64: cv.toDataURL("image/jpeg", 0.72).split(",")[1], width, height };
};

// Scarica la prima foto di ogni classificazione (max 3 alla volta).
// thumbCache: Map condivisa con StoricoTab per riusare le miniature già caricate.
export const fetchPhotosFor = async (list, thumbCache, onProgress) => {
  const photos = new Map();
  let done = 0;
  const worker = async (c) => {
    try {
      let p = c.id && thumbCache.get(c.id);
      if (p === undefined) {
        const full = await getClassification(c.id);
        const raw = (full?.photos || [])[0];
        p = raw ? await resolvePhoto(raw) : "";
        thumbCache.set(c.id, p);
      }
      if (p) photos.set(c.id, p);
    } catch {
      // foto saltata, la riga resta senza immagine
    } finally {
      done++;
      onProgress?.(done, list.length);
    }
  };
  const queue = [...list];
  const runners = Array.from({ length: 3 }, async () => {
    while (queue.length) await worker(queue.shift());
  });
  await Promise.all(runners);
  return photos;
};

export const downloadXlsx = async (list, photos, lang, t) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Storico");
  ws.columns = [
    { header: HEADERS(t)[0], width: 11 },
    { header: HEADERS(t)[1], width: 24 },
    { header: HEADERS(t)[2], width: 42 },
    { header: HEADERS(t)[3], width: 9 },
    { header: HEADERS(t)[4], width: 10 },
    { header: HEADERS(t)[5], width: 14 },
    { header: "Foto", width: 20 },
  ];
  ws.getRow(1).font = { bold: true };

  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    const row = ws.addRow([...rowValues(c, lang, t), ""]);
    const p = c.id ? photos.get(c.id) : null;
    if (p) {
      try {
        const img = await downscale(p);
        row.height = Math.max(20, Math.round(img.height * 0.78));
        const imgId = wb.addImage({ base64: img.base64, extension: "jpeg" });
        ws.addImage(imgId, {
          tl: { col: 6, row: row.number - 1 },
          ext: { width: img.width, height: img.height },
        });
      } catch {
        // riga senza foto
      }
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `storico-metal-class_${stamp()}.xlsx`
  );
};

export const downloadPdf = (list, lang, t, summary) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFontSize(17);
  doc.setTextColor(23, 18, 46);
  doc.text("metal-class — Storico classificazioni", 14, 16);
  doc.setFontSize(9);
  doc.setTextColor(130, 130, 150);
  doc.text(summary, 14, 23);
  doc.text(
    `${t.reserved.exportedOn || "Generato il"} ${new Date().toLocaleString(lang === "it" ? "it-IT" : "en-GB")} · ${list.length} record`,
    14,
    29
  );

  autoTable(doc, {
    startY: 34,
    head: [HEADERS(t)],
    body: list.map((c) => rowValues(c, lang, t)),
    styles: { fontSize: 8, cellPadding: 2, textColor: [40, 36, 60] },
    headStyles: { fillColor: [52, 211, 153], textColor: [23, 18, 46], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [244, 244, 248] },
    columnStyles: { 2: { cellWidth: 62 } },
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 175);
    doc.text(`${i} / ${pages}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
  }

  doc.save(`storico-metal-class_${stamp()}.pdf`);
};
